import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Button from "../../common/Button";
import SideDrawer from "../../common/SideDrawer";
import Autocomplete from "../../common/AutoComplete";
import apiService from "../../../services/ApiService";
import { apiRoutes } from "../../../shared/apiRoutes";
import { useAuthorization } from "../../../auth/useAuthorization";
import { Role } from "../../../auth/roles";
import { AutoCompleteOption, Party } from "../../../shared/interfaces";

interface CreateUserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** When set, drawer loads and updates this user. */
  userId?: number | null;
  /** Party/role hints from the users grid (used if GET detail omits them). */
  initialUser?: {
    partyId?: number | null;
    partyCode?: string;
    partyName?: string;
    role?: string;
  } | null;
}

interface UserFormValues {
  firstName: string;
  lastName: string;
  userName: string;
  password: string;
  role: string;
  partyId: string;
}

interface RoleOption {
  id: number | string;
  name: string;
  code?: string;
}

function compactRoleName(name: string | undefined): string {
  return (name || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function roleOptionLabel(name: string): string {
  const compact = compactRoleName(name);
  if (compact === "angadiyauser") return "Angadiya User";
  if (compact === "angadiya") return "Angadiya";
  return name;
}

/** RoleMasters can contain both "Angadiya User" and "AngadiyaUser". Show one option. */
function dedupeRoles(roles: RoleOption[]): RoleOption[] {
  const chosen = new Map<string, RoleOption>();
  for (const role of roles) {
    const compact = compactRoleName(role.name);
    const existing = chosen.get(compact);
    if (!existing || (existing.name.includes(" ") && !role.name.includes(" "))) {
      chosen.set(compact, role);
    }
  }
  return roles.filter((role) => chosen.get(compactRoleName(role.name)) === role);
}

interface UserDetailResponse {
  id?: number;
  firstName?: string;
  lastName?: string;
  userName?: string;
  username?: string;
  role?: string;
  roleId?: number | string;
  RoleId?: number | string;
  partyId?: number | null;
  PartyId?: number | null;
  partyCode?: string;
  PartyCode?: string;
  partyName?: string;
  PartyName?: string;
  party?: {
    id?: number;
    partyId?: number;
    partyCode?: string;
    partyName?: string;
  } | null;
}

const emptyFormValues: UserFormValues = {
  firstName: "",
  lastName: "",
  userName: "",
  password: "",
  role: "",
  partyId: "",
};

const isPartnerRoleName = (name?: string | null) =>
  (name || "").trim().toLowerCase() === "partner";

const normalizeRolesList = (response: unknown): RoleOption[] => {
  const raw = (() => {
    if (Array.isArray(response)) return response;
    const r = response as { data?: RoleOption[]; items?: RoleOption[]; roles?: RoleOption[] };
    if (Array.isArray(r?.data)) return r.data;
    if (Array.isArray(r?.items)) return r.items;
    if (Array.isArray(r?.roles)) return r.roles;
    return [];
  })();

  return raw.map((role) => {
    const r = role as Record<string, unknown>;
    const idRaw = r.id ?? r.Id;
    const id: number | string =
      typeof idRaw === "number" || typeof idRaw === "string" ? idRaw : "";
    return {
      id,
      name: String(r.name ?? r.Name ?? ""),
      code: r.code != null || r.Code != null ? String(r.code ?? r.Code) : undefined,
    };
  });
};

const toPartyOption = (p: {
  id: number;
  partyCode?: string;
  partyName?: string;
}): AutoCompleteOption => ({
  id: p.id,
  name: p.partyName || "",
  code: p.partyCode || "",
  displayName: `${p.partyCode || ""}${p.partyName ? ` - ${p.partyName}` : ""}`.trim() || String(p.id),
});

const normalizePartyLite = (raw: Record<string, unknown>): Party | null => {
  const id = Number(raw.id ?? raw.Id);
  if (!id || Number.isNaN(id)) return null;
  return {
    id,
    partyCode: String(raw.partyCode ?? raw.PartyCode ?? ""),
    partyName: String(raw.partyName ?? raw.PartyName ?? ""),
    phone: String(raw.phone ?? raw.Phone ?? ""),
    openingBalance: String(raw.openingBalance ?? raw.OpeningBalance ?? ""),
    city: String(raw.city ?? raw.City ?? ""),
  };
};

const mergeUserDetail = (
  detail: UserDetailResponse | null,
  seed?: CreateUserDialogProps["initialUser"],
): UserDetailResponse | null => {
  if (!detail && !seed) return null;
  return {
    ...(detail || {}),
    role: detail?.role || seed?.role,
    partyId: detail?.partyId ?? detail?.PartyId ?? seed?.partyId ?? null,
    partyCode: detail?.partyCode || detail?.PartyCode || seed?.partyCode,
    partyName: detail?.partyName || detail?.PartyName || seed?.partyName,
  };
};

const normalizeUserDetail = (raw: unknown): UserDetailResponse | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  return {
    id: Number(r.id ?? r.Id) || undefined,
    firstName: String(r.firstName ?? r.FirstName ?? ""),
    lastName: String(r.lastName ?? r.LastName ?? ""),
    userName: String(r.userName ?? r.UserName ?? r.username ?? r.Username ?? ""),
    role: r.role != null || r.Role != null ? String(r.role ?? r.Role) : undefined,
    roleId: (r.roleId ?? r.RoleId) as number | string | undefined,
    partyId: (r.partyId ?? r.PartyId) as number | null | undefined,
    partyCode: r.partyCode != null || r.PartyCode != null ? String(r.partyCode ?? r.PartyCode) : undefined,
    partyName: r.partyName != null || r.PartyName != null ? String(r.partyName ?? r.PartyName) : undefined,
  };
};

const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  isOpen,
  onClose,
  userId = null,
  initialUser = null,
}) => {
  const isEdit = userId != null && Number(userId) > 0;
  const { hasRole } = useAuthorization();
  const isAdmin = hasRole(Role.ADMIN);
  const isAngadiyaCreator = hasRole(Role.ANGADIYA) && !isAdmin;
  const canAssignAngadiyaRoles = isAdmin || hasRole(Role.ANGADIYA);
  const initialUserRef = useRef(initialUser);
  initialUserRef.current = initialUser;

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const [parties, setParties] = useState<Party[]>([]);
  const [loadingParties, setLoadingParties] = useState(false);
  const [partiesError, setPartiesError] = useState<string | null>(null);
  /** Bound party loaded for edit — used when resolving Autocomplete display from partyId. */
  const [savedPartyOption, setSavedPartyOption] = useState<AutoCompleteOption | null>(null);

  const [formInitial, setFormInitial] = useState<UserFormValues>(emptyFormValues);
  const [formReady, setFormReady] = useState(false);
  const [loadingUser, setLoadingUser] = useState(false);
  const [userLoadError, setUserLoadError] = useState<string | null>(null);

  const partyOptions: AutoCompleteOption[] = useMemo(
    () =>
      parties.map((p) => ({
        id: p.id,
        name: p.partyName || "",
        code: p.partyCode || "",
        displayName: `${p.partyCode || ""}${p.partyName ? ` - ${p.partyName}` : ""}`.trim(),
      })),
    [parties],
  );

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .required("First Name is required")
          .max(100, "First Name must be at most 100 characters"),
        lastName: Yup.string()
          .required("Last Name is required")
          .max(100, "Last Name must be at most 100 characters"),
        userName: Yup.string()
          .required("User Name is required")
          .min(3, "User Name must be at least 3 characters")
          .max(20, "User Name must be at most 20 characters"),
        password: isEdit
          ? Yup.string()
              .transform((v) => (v == null ? "" : v))
              .test(
                "optional-password",
                "Password must be at least 8 characters",
                (value) => !value || value.length === 0 || value.length >= 8,
              )
              .max(100, "Password must be at most 100 characters")
          : Yup.string()
              .required("Password is required")
              .min(8, "Password must be at least 8 characters")
              .max(100, "Password must be at most 100 characters"),
        role: Yup.string().required("Role is required"),
        partyId: Yup.string().when("role", {
          is: (roleId: string) => {
            const selected = roles.find((r) => String(r.id) === String(roleId));
            return isPartnerRoleName(selected?.name);
          },
          then: (schema) => schema.required("Party is required for Partner role"),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
    [roles, isEdit],
  );

  const resolveRoleId = useCallback((rolesList: RoleOption[], detail: UserDetailResponse): string => {
    const roleIdRaw = detail.roleId ?? detail.RoleId;
    if (roleIdRaw != null && String(roleIdRaw) !== "") {
      const byId = rolesList.find((r) => String(r.id) === String(roleIdRaw));
      if (byId) return String(byId.id);
    }
    const roleName = (detail.role || "").trim().toLowerCase();
    if (roleName) {
      const byName = rolesList.find((r) => (r.name || "").trim().toLowerCase() === roleName);
      if (byName) return String(byName.id);
    }
    return "";
  }, []);

  const resolvePartyOption = useCallback(
    (
      partiesList: Party[],
      detail: UserDetailResponse | null,
      seed?: CreateUserDialogProps["initialUser"],
    ): AutoCompleteOption | null => {
      const nested = detail?.party;
      const partyId =
        detail?.partyId ??
        detail?.PartyId ??
        nested?.id ??
        nested?.partyId ??
        seed?.partyId ??
        null;
      if (partyId == null || Number(partyId) <= 0) return null;

      const fromList = partiesList.find((p) => Number(p.id) === Number(partyId));
      if (fromList) return toPartyOption(fromList);

      const code =
        detail?.partyCode ||
        detail?.PartyCode ||
        nested?.partyCode ||
        seed?.partyCode ||
        "";
      const name =
        detail?.partyName ||
        detail?.PartyName ||
        nested?.partyName ||
        seed?.partyName ||
        "";
      return toPartyOption({ id: Number(partyId), partyCode: code, partyName: name });
    },
    [],
  );

  const loadRoles = useCallback(async (): Promise<RoleOption[]> => {
    setLoadingRoles(true);
    setRolesError(null);
    try {
      let response: unknown;
      try {
        response = await apiService.get(apiRoutes.roles.list);
      } catch (primaryError) {
        console.warn("Primary roles endpoint failed, trying fallback:", primaryError);
        response = await apiService.get(apiRoutes.users.roles);
      }

      let filteredRoles = normalizeRolesList(response);
      if (isAngadiyaCreator) {
        filteredRoles = filteredRoles.filter((role) => {
          const compact = compactRoleName(role.name);
          return compact === "angadiya" || compact === "angadiyauser";
        });
        filteredRoles.sort((a, b) => {
          const rank = (name: string) => (compactRoleName(name) === "angadiyauser" ? 0 : 1);
          return rank(a.name) - rank(b.name);
        });
      } else if (!isAdmin) {
        filteredRoles = filteredRoles.filter((role) => {
          const roleName = (role.name || "").toLowerCase();
          const compact = compactRoleName(role.name);
          if (roleName === "admin" || compact === "angadiyaadmin") return false;
          if ((compact === "angadiya" || compact === "angadiyauser") && !canAssignAngadiyaRoles) return false;
          return true;
        });
      }
      filteredRoles = dedupeRoles(filteredRoles);
      setRoles(filteredRoles);
      return filteredRoles;
    } catch (error: unknown) {
      console.error("Error fetching roles:", error);
      setRolesError("Failed to load roles");
      setRoles([]);
      return [];
    } finally {
      setLoadingRoles(false);
    }
  }, [isAdmin, isAngadiyaCreator, canAssignAngadiyaRoles]);

  const loadParties = useCallback(async (): Promise<Party[]> => {
    setLoadingParties(true);
    setPartiesError(null);
    try {
      const response = await apiService.post<{ data?: Party[]; total?: number } | Party[]>(
        apiRoutes.parties.lite,
        { page: 1, pageSize: 10000 },
      );
      const list = Array.isArray(response)
        ? response
        : Array.isArray((response as { data?: Party[] })?.data)
          ? (response as { data: Party[] }).data
          : [];
      const normalized = list
        .map((item) => normalizePartyLite(item as Record<string, unknown>))
        .filter((p): p is Party => p != null);
      setParties(normalized);
      return normalized;
    } catch (error: unknown) {
      console.error("Error fetching parties:", error);
      setParties([]);
      setPartiesError("Failed to load parties");
      return [];
    } finally {
      setLoadingParties(false);
    }
  }, []);

  const loadUserDetail = useCallback(async (id: number): Promise<UserDetailResponse | null> => {
    setLoadingUser(true);
    setUserLoadError(null);
    try {
      const response = await apiService.get<UserDetailResponse | { data?: UserDetailResponse }>(
        apiRoutes.users.getById(id),
      );

      const nested =
        response &&
        typeof response === "object" &&
        "data" in response &&
        (response as { data?: unknown }).data &&
        typeof (response as { data?: unknown }).data === "object"
          ? normalizeUserDetail((response as { data: unknown }).data)
          : null;
      const direct = normalizeUserDetail(response);
      return nested || direct;
    } catch (error: unknown) {
      console.error("Error loading user:", error);
      const msg =
        (error as { message?: string; error?: string })?.message ||
        (error as { error?: string })?.error ||
        "Failed to load user details";
      setUserLoadError(String(msg));
      return null;
    } finally {
      setLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setFormReady(false);
      setFormInitial(emptyFormValues);
      setSavedPartyOption(null);
      setUserLoadError(null);
      setPartiesError(null);
      setRolesError(null);
      return;
    }

    let cancelled = false;

    const bootstrap = async () => {
      setFormReady(false);
      setSavedPartyOption(null);
      setUserLoadError(null);

      const seed = initialUserRef.current;
      if (isEdit && seed?.partyId && Number(seed.partyId) > 0) {
        setSavedPartyOption(
          toPartyOption({
            id: Number(seed.partyId),
            partyCode: seed.partyCode,
            partyName: seed.partyName,
          }),
        );
      }

      const [rolesList, partiesList] = await Promise.all([loadRoles(), loadParties()]);
      if (cancelled) return;

      if (isEdit && userId) {
        const seed = initialUserRef.current;
        const detail = await loadUserDetail(Number(userId));
        if (cancelled) return;

        if (!detail && !seed) {
          setFormInitial(emptyFormValues);
          setFormReady(true);
          return;
        }

        const mergedDetail = mergeUserDetail(detail, seed);
        let rolesForForm = rolesList;
        if (isAngadiyaCreator && mergedDetail?.role) {
          const currentCompact = compactRoleName(mergedDetail.role);
          const alreadyListed = rolesList.some((role) => compactRoleName(role.name) === currentCompact);
          if (!alreadyListed) {
            const currentId = mergedDetail.roleId ?? mergedDetail.RoleId ?? mergedDetail.role;
            rolesForForm = [...rolesList, { id: currentId, name: mergedDetail.role }];
            setRoles(rolesForForm);
          }
        }
        const roleValue = resolveRoleId(rolesForForm, mergedDetail || {});
        const partyOption = resolvePartyOption(partiesList, mergedDetail, seed);
        // Prefer the option instance from the loaded list so Combobox matches by id
        const matchedParty =
          partyOption &&
          partiesList.some((p) => Number(p.id) === Number(partyOption.id))
            ? toPartyOption(
                partiesList.find((p) => Number(p.id) === Number(partyOption.id))!,
              )
            : partyOption;
        setSavedPartyOption(matchedParty);

        setFormInitial({
          firstName: mergedDetail?.firstName || detail?.firstName || "",
          lastName: mergedDetail?.lastName || detail?.lastName || "",
          userName: mergedDetail?.userName || detail?.userName || "",
          password: "",
          role: roleValue,
          partyId: matchedParty ? String(matchedParty.id) : "",
        });
        setFormReady(true);
        return;
      }

      const userRole = rolesList.find(
        (r) => r.name?.toLowerCase() === "user" || r.code?.toLowerCase() === "user",
      );
      const defaultRole = userRole
        ? String(userRole.id || "")
        : rolesList.length > 0
          ? String(rolesList[0].id || "")
          : "";

      setFormInitial({
        ...emptyFormValues,
        role: defaultRole,
      });
      setFormReady(true);
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    isEdit,
    userId,
    loadRoles,
    loadParties,
    loadUserDetail,
    resolveRoleId,
    resolvePartyOption,
    isAngadiyaCreator,
  ]);

  const handleSubmit = async (
    values: UserFormValues,
    formikHelpers?: { setFieldError?: (field: string, message: string) => void },
  ) => {
    try {
      const roleId = typeof values.role === "string" ? parseInt(values.role, 10) : Number(values.role);

      if (!roleId || isNaN(roleId) || roleId <= 0) {
        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: { message: "Please select a valid role", type: "error" },
          }),
        );
        return;
      }

      const selectedRole = roles.find((r) => String(r.id) === String(values.role));
      if (!selectedRole?.name) {
        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: { message: "Please select a valid role", type: "error" },
          }),
        );
        return;
      }

      const isPartner = isPartnerRoleName(selectedRole.name);
      const partyIdNum = values.partyId ? Number(values.partyId) : NaN;

      if (isPartner && (!partyIdNum || isNaN(partyIdNum) || partyIdNum <= 0)) {
        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: { message: "Please select a party for the Partner user", type: "error" },
          }),
        );
        return;
      }

      if (isEdit && userId) {
        const updatePayload: Record<string, unknown> = {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          userName: values.userName.trim(),
          role: selectedRole.name,
        };

        if (values.password.trim()) {
          updatePayload.password = values.password;
        }

        if (isPartner) {
          updatePayload.PartyId = partyIdNum;
          updatePayload.partyId = partyIdNum;
        } else {
          updatePayload.PartyId = null;
          updatePayload.partyId = null;
        }

        await apiService.put(apiRoutes.users.update(userId), updatePayload);

        window.dispatchEvent(
          new CustomEvent("show-toast", {
            detail: { message: "User updated successfully", type: "success" },
          }),
        );
        onClose();
        window.dispatchEvent(new CustomEvent("user-updated"));
        return;
      }

      const createPayload: Record<string, unknown> = {
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        userName: values.userName.trim(),
        password: values.password,
        RoleId: roleId,
        roleId,
      };

      if (isPartner) {
        createPayload.PartyId = partyIdNum;
        createPayload.partyId = partyIdNum;
      }

      await apiService.post(apiRoutes.users.create, createPayload);

      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: { message: "User created successfully", type: "success" },
        }),
      );
      onClose();
      window.dispatchEvent(new CustomEvent("user-created"));
    } catch (error: any) {
      console.error(isEdit ? "Error updating user:" : "Error creating user:", error);

      const errorMessage =
        error?.details ||
        error?.message ||
        error?.error ||
        error?.response?.data?.details ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (isEdit ? "Failed to update user. Please try again." : "Failed to create user. Please try again.");

      if (/already exists/i.test(String(errorMessage))) {
        formikHelpers?.setFieldError?.("userName", String(errorMessage));
      }

      window.dispatchEvent(
        new CustomEvent("show-toast", {
          detail: { message: String(errorMessage), type: "error" },
        }),
      );
    }
  };

  const drawerBusy = !formReady || loadingUser || loadingRoles;

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? "Edit User" : "Create User"}
      width="w-[min(24rem,calc(100vw-1rem))]"
    >
      {userLoadError && (
        <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {userLoadError}
        </div>
      )}

      {drawerBusy ? (
        <div className="py-8 text-center text-sm text-slate-500">
          {loadingUser ? "Loading user details…" : "Loading…"}
        </div>
      ) : (
        <Formik
          key={isOpen ? `${isEdit ? `edit-${userId}` : "create"}-${formInitial.role}-${formInitial.partyId}` : "closed"}
          initialValues={formInitial}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting, values, setFieldValue, setFieldTouched }) => {
            const selectedRole = roles.find((r) => String(r.id) === String(values.role));
            const showPartyField =
              isPartnerRoleName(selectedRole?.name) ||
              Boolean(values.partyId || savedPartyOption);

            const resolvePartyFromId = (partyId: string): AutoCompleteOption | null => {
              if (!partyId) return null;
              const fromOptions = partyOptions.find((o) => String(o.id) === String(partyId));
              if (fromOptions) return fromOptions;
              if (savedPartyOption && String(savedPartyOption.id) === String(partyId)) {
                return savedPartyOption;
              }
              return null;
            };

            const partySelectedRaw = resolvePartyFromId(values.partyId);
            const partyFieldOptions =
              partySelectedRaw &&
              !partyOptions.some((o) => String(o.id) === String(partySelectedRaw.id))
                ? [partySelectedRaw, ...partyOptions]
                : partyOptions;
            const partySelected = partySelectedRaw
              ? partyFieldOptions.find((o) => String(o.id) === String(partySelectedRaw.id)) ??
                partySelectedRaw
              : null;

            return (
              <Form className="min-w-0 space-y-4">
                <div>
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    First Name<span className="text-red-500">*</span>
                  </label>
                  <Field
                    name="firstName"
                    type="text"
                    placeholder="First Name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <ErrorMessage name="firstName" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    Last Name<span className="text-red-500">*</span>
                  </label>
                  <Field
                    name="lastName"
                    type="text"
                    placeholder="Last Name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <ErrorMessage name="lastName" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    User Name<span className="text-red-500">*</span>
                  </label>
                  <Field
                    name="userName"
                    type="text"
                    placeholder="User Name"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <ErrorMessage name="userName" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    Password{!isEdit && <span className="text-red-500">*</span>}
                  </label>
                  <Field
                    name="password"
                    type="password"
                    placeholder={isEdit ? "Leave blank to keep current password" : "Password"}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoComplete={isEdit ? "new-password" : "new-password"}
                  />
                  {isEdit && (
                    <p className="mt-1 text-xs text-slate-500">Leave blank to keep the existing password.</p>
                  )}
                  <ErrorMessage name="password" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                <div>
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    Role<span className="text-red-500">*</span>
                  </label>
                  <Field
                    name="role"
                    as="select"
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={loadingRoles}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const nextRoleId = e.target.value;
                      setFieldValue("role", nextRoleId);
                      const nextRole = roles.find((r) => String(r.id) === String(nextRoleId));
                      if (!isPartnerRoleName(nextRole?.name)) {
                        setFieldValue("partyId", "");
                      } else if (savedPartyOption && !values.partyId) {
                        setFieldValue("partyId", String(savedPartyOption.id));
                      }
                    }}
                  >
                    <option value="">
                      {loadingRoles
                        ? "Loading roles..."
                        : rolesError
                          ? "Error loading roles"
                          : "Select Role"}
                    </option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {roleOptionLabel(role.name)}
                      </option>
                    ))}
                  </Field>
                  {rolesError && <div className="text-red-500 text-xs mt-1">{rolesError}</div>}
                  <ErrorMessage name="role" component="div" className="text-red-500 text-xs mt-1" />
                </div>

                {showPartyField && (
                  <div>
                    <Field type="hidden" name="partyId" />
                    <label className="block font-medium text-sm text-gray-700 mb-1">
                      Party<span className="text-red-500">*</span>
                    </label>
                    <p className="text-xs text-slate-500 mb-1.5">
                      Partner users must be bound to a party. One party can only bind to one Partner user.
                    </p>
                    <Autocomplete
                      options={partyFieldOptions}
                      selected={partySelected}
                      setSelected={(option) => {
                        setFieldValue("partyId", option ? String(option.id) : "");
                        setFieldTouched("partyId", true, false);
                      }}
                      className="w-full"
                      props={{
                        placeholder: loadingParties
                          ? "Loading parties..."
                          : partiesError
                            ? "Error loading parties"
                            : "Search party code or name",
                        disabled: loadingParties || Boolean(partiesError),
                      }}
                    />
                    {partiesError && <div className="text-red-500 text-xs mt-1">{partiesError}</div>}
                    <ErrorMessage name="partyId" component="div" className="text-red-500 text-xs mt-1" />
                  </div>
                )}

                <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    onClick={onClose}
                    className="w-full rounded-md bg-gray-400 px-4 py-2 text-white hover:bg-gray-500 sm:w-auto"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting || loadingRoles || (showPartyField && loadingParties)}
                    className="w-full rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </SideDrawer>
  );
};

export default CreateUserDialog;
